import { useState, useEffect } from "react";
import AllCourses from "../AllCourses";
import axios from "axios";
import { G_API, PAGE_LIMIT } from "../../constants/globalVar";
import PageSlider from "../PageSlider";
import { FaRegTimesCircle, FaSearch } from "react-icons/fa";
import { useLocation, useSearchParams } from "react-router-dom";
import { COURSETYPES } from "../../constants/courseTypes";
import PlaceholderSearchBar from "../Placeholders/PlaceholderSearch";

const CoursePage = () => {
    const location = useLocation();
    const [search, setSearch] = useState("");
    const [courseType, setCourseType] = useState("");
    const [searchParams] = useSearchParams();

    const [items, setItems] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [loadingItem, setLoadingItem] = useState(true);
    const [error, setError] = useState(null);

    const resetSearch = () => {
        setSearch("");
        setCourseType("");
    };

    useEffect(() => {
        const query = new URLSearchParams(location.search);
        const initialSearch = query.get("search") || "";
        setSearch(initialSearch);
    }, [location.search]);

    // Update search and course type keywords
    useEffect(() => {
        const paramSearch = searchParams.get("search") || "";
        const paramCourseType = searchParams.get("course-type") || "";
        if (paramSearch) setSearch(paramSearch);
        if (paramCourseType) setCourseType(paramCourseType);
        setCurrentPage(1);
    }, [searchParams]);

    // Run when search, courseType, or currentPage changes
    useEffect(() => {
        setLoadingItem(true);

        const fetchItems = async (page = currentPage) => {
            const course_type = courseType ?? '';
            setError(null);

            try {
                const response = await axios.post(`${G_API}courses/COURSE_LIST`, {
                    search: search.trim(),
                    course_type: course_type,
                    page: page,
                    limit: PAGE_LIMIT,
                });

                const items = response.data.DATAS || [];
                const current = response.data.CURRENT_PAGE || 1;
                const last = response.data.LAST_PAGE || 1;

                setItems(items);
                setCurrentPage(current);
                setLastPage(last);
            } catch (err) {
                setError("Something went wrong.");
                console.error("Error fetching pagination items:", err);
            } finally {
                setLoadingItem(false);
                setLoading(false);
            }
        };

        const delayDebounce = setTimeout(() => {
            fetchItems();
        }, 300);

        return () => clearTimeout(delayDebounce);
    }, [search, courseType, currentPage]);


    return (
        <>
            <div id="header-carousel" className="carousel slide" data-bs-ride="carousel" >
                <div className="carousel-inner" >
                    <PageSlider inputType="course" />
                </div>
            </div>
            {/* Start SearchBar */}
            { loading ? (
                <PlaceholderSearchBar />
            ) : (
                <div className="mt-4 mb-6 text-center" style={{ height: '50px' }}>
                    <div
                        className="bg-primary-blue rounded rounded-4 p-3 p-m-5 w-100 hm-100 position-absolute"
                        style={{
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 'calc(100% - 30px)',
                            maxWidth: '80vw'
                        }}
                    >
                        <h2 className="fs-2 text-white mb-2">Find a course</h2>
                        <div className="row g-3">
                            {/* You said ignore dropdown so we leave it here but do nothing */}
                            <div className="col-md-4 col-12">
                                <select
                                    className="form-control"
                                    value={courseType}
                                    onChange={(e) => {
                                        setCourseType(e.target.value);
                                    }}
                                >
                                    <option value="">Select a course type</option>
                                    {COURSETYPES.map(course_type => (
                                        <option value={course_type.value} key={course_type.value}>{course_type.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-8 col-12">
                                <div className="input-group mb-3">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Course name, keywords..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                    <button className="btn bg-search" type="button">
                                        {search.length > 0 ? (
                                            <FaRegTimesCircle onClick={resetSearch} />
                                        ) : (
                                            <FaSearch />
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* End Searchbar */}
            <section id="course">
                <div className="container-xxl my-5 py-5">
                    <div className="container py-5">
                        <div className="row g-4 justify-content-center list">
                            <div className="item">
                                {error && <p className="text-danger">{error}</p>}

                                {loadingItem ? (
                                    <div className="text-center my-4">
                                        <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {Array.isArray(items) ? (
                                            items.map(item => (
                                                <AllCourses
                                                    key={item.id}
                                                    item={item}
                                                    style={{
                                                        width: "calc(33.33% - 20px)",
                                                        margin: "20px",
                                                        textAlign: "center",
                                                    }}
                                                />
                                            ))
                                        ) : (
                                            <p>No courses found or data format invalid.</p>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Pagination */}
                        { !loadingItem && items.length > 0 && (
                            <div className="d-flex justify-content-center mt-4">
                                <nav>
                                    <ul className="pagination">
                                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                            <button className="page-link" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>
                                                Previous
                                            </button>
                                        </li>
                                        {Array.from({ length: lastPage }, (_, i) => i + 1).map(page => (
                                            <li key={page} className={`page-item ${page === currentPage ? "active" : ""}`}>
                                                <button className="page-link" onClick={() => setCurrentPage(page)}>
                                                    {page}
                                                </button>
                                            </li>
                                        ))}
                                        <li className={`page-item ${currentPage === lastPage ? "disabled" : ""}`}>
                                            <button className="page-link" onClick={() => setCurrentPage(prev => Math.min(prev + 1, lastPage))}>
                                                Next
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        )}
                        {/* End Pagination */}
                    </div>
                </div>
            </section>
        </>
    );
}

export default CoursePage;
